import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import api from '../services/api';
import logo from '../images/logo.png';

const Cadastro = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cpf, setCpf] = useState('');
  const navigate = useNavigate();

  // Função para formatar CPF
  const formatCPF = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    }
    return value;
  };

  // Função para formatar telefone
  const formatTelefone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      // Telefone fixo: (00) 0000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    } else if (numbers.length <= 11) {
      // Celular: (00) 00000-0000
      return numbers
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{5})(\d)/, '$1-$2');
    }
    return value;
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();


  const onSubmit = async (data) => {
    if (data.senha !== data.confirmarSenha) {
      alert('As senhas não coincidem');
      return;
    }

    if (!cpf || cpf.replace(/\D/g, '').length !== 11) {
      alert('CPF é obrigatório e deve ter 11 dígitos');
      return;
    }

    setLoading(true);
    try {
      // Formatar telefone se necessário
      let telefoneFormatado = data.telefone;
      if (telefoneFormatado && !telefoneFormatado.includes('(')) {
        telefoneFormatado = telefoneFormatado.replace(/\D/g, '');
        if (telefoneFormatado.length === 11) {
          telefoneFormatado = `(${telefoneFormatado.slice(0, 2)}) ${telefoneFormatado.slice(2, 7)}-${telefoneFormatado.slice(7)}`;
        } else if (telefoneFormatado.length === 10) {
          telefoneFormatado = `(${telefoneFormatado.slice(0, 2)}) ${telefoneFormatado.slice(2, 6)}-${telefoneFormatado.slice(6)}`;
        }
      }

      await api.post('/auth/register', {
        nome: data.nome,
        cpf: cpf,
        email: data.email || null,
        senha: data.senha,
        telefone: telefoneFormatado || null,
        tipo: 'morador',
      });

      alert('Cadastro realizado com sucesso! Aguarde o administrador vincular você ao condomínio.');
      navigate('/login');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        'Erro ao realizar cadastro. Tente novamente.';
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-container flex items-center justify-center bg-primary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-xl border border-primary-100 px-8 py-10 space-y-8">
          <div className="mx-auto flex flex-col items-center justify-center space-y-3">
            <img
              src={logo}
              alt="Logo CondoSmart"
              className="h-20 w-auto drop-shadow"
            />
            <h2 className="text-center text-2xl font-extrabold text-primary-700">
              Criar Conta
            </h2>
            <p className="text-center text-sm font-medium text-primary-600">
              Crie sua conta. O administrador vinculará você ao condomínio.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="nome" className="label">
                  Nome Completo *
                </label>
                <input
                  {...register('nome', {
                    required: 'Nome é obrigatório',
                  })}
                  type="text"
                  className={errors.nome ? 'input-error' : 'input'}
                  placeholder="Seu nome completo"
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.nome.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="cpf" className="label">
                  CPF *
                </label>
                <input
                  type="text"
                  value={cpf}
                  onChange={(e) => setCpf(formatCPF(e.target.value))}
                  className="input"
                  placeholder="000.000.000-00"
                  maxLength={14}
                />
              </div>

              <div>
                <label htmlFor="email" className="label">
                  Email (opcional)
                </label>
                <input
                  {...register('email', {
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Email inválido',
                    },
                  })}
                  type="email"
                  className={errors.email ? 'input-error' : 'input'}
                  placeholder="seu@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="telefone" className="label">
                  Telefone (opcional)
                </label>
                <input
                  {...register('telefone')}
                  type="tel"
                  className={errors.telefone ? 'input-error' : 'input'}
                  placeholder="(11) 99999-9999"
                  maxLength={15}
                  onChange={(e) => {
                    const formatted = formatTelefone(e.target.value);
                    e.target.value = formatted;
                    setValue('telefone', formatted, { shouldValidate: false });
                  }}
                />
                {errors.telefone && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.telefone.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="senha" className="label">
                  Senha *
                </label>
                <div className="relative">
                  <input
                    {...register('senha', {
                      required: 'Senha é obrigatória',
                      minLength: {
                        value: 6,
                        message: 'Senha deve ter pelo menos 6 caracteres',
                      },
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className={errors.senha ? 'input-error pr-10' : 'input pr-10'}
                    placeholder="Sua senha"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.senha && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.senha.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmarSenha" className="label">
                  Confirmar Senha *
                </label>
                <div className="relative">
                  <input
                    {...register('confirmarSenha', {
                      required: 'Confirmação de senha é obrigatória',
                    })}
                    type={showConfirmPassword ? 'text' : 'password'}
                    className={
                      errors.confirmarSenha ? 'input-error pr-10' : 'input pr-10'
                    }
                    placeholder="Confirme sua senha"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() =>
                      setShowConfirmPassword(!showConfirmPassword)
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.confirmarSenha && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.confirmarSenha.message}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-sm font-semibold text-primary-600 hover:text-primary-500 transition-colors"
              >
                Já tem uma conta? Faça login
              </button>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary px-6 shadow-lg shadow-primary-200/60"
              >
                {loading ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : null}
                {loading ? 'Criando conta...' : 'Criar Conta'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Cadastro;

